import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Edit2, Share2 } from 'lucide-react';
import { BrandLogoLink } from './BrandLogoLink';
import { ThemeToggle } from '../UI/ThemeToggle';
import { LanguageSelector } from '../UI/LanguageSelector';
import { SettingsButton } from '../UI/SettingsButton';
import { shareRoomLink } from '../../utils/shareRoom';
import { EmojiNameInput } from '../UI/EmojiNameInput';

interface HeaderProps {
  roomId?: string;
  roomName?: string;
  onCreateNewRoom?: () => void;
  onRoomNameUpdate?: (name: string) => Promise<void>;
  roomMode?: boolean;
}

export function Header({
  roomId,
  roomName,
  onCreateNewRoom,
  onRoomNameUpdate,
  roomMode = false,
}: HeaderProps) {
  const { t } = useTranslation();
  const displayRoomName = roomName ?? t('calendar.defaultRoomName');
  const [copied, setCopied] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(displayRoomName);

  useEffect(() => {
    setEditedName(displayRoomName);
  }, [displayRoomName]);

  const handleShare = async () => {
    if (!roomId) return;
    const result = await shareRoomLink({
      roomId,
      title: t('share.shareTitle'),
      text: t('share.shareText'),
    });
    if (result === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNameClick = () => {
    if (roomId && onRoomNameUpdate) {
      setEditedName(displayRoomName);
      setIsEditingName(true);
    }
  };

  const handleNameBlur = async () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== displayRoomName && onRoomNameUpdate) {
      try {
        await onRoomNameUpdate(trimmedName);
      } catch (error) {
        console.error('Failed to update room name:', error);
        setEditedName(displayRoomName);
      }
    } else {
      setEditedName(displayRoomName);
    }
    setIsEditingName(false);
  };

  if (roomMode && roomId) {
    return (
      <header className="sticky top-0 z-50 px-0.5 pb-5 pt-1">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <BrandLogoLink />
            {isEditingName ? (
              <EmojiNameInput
                value={editedName}
                onChange={setEditedName}
                onBlur={handleNameBlur}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleNameBlur();
                  if (e.key === 'Escape') {
                    setEditedName(displayRoomName);
                    setIsEditingName(false);
                  }
                }}
                placeholder={t('header.roomName')}
                autoFocus
                maxLength={50}
              />
            ) : onRoomNameUpdate ? (
              <button
                type="button"
                onClick={handleNameClick}
                className="group flex min-w-0 max-w-[calc(100vw-8rem)] items-center gap-1.5 rounded-lg py-0.5 pr-0.5 text-left transition-colors hover:bg-room-surface-2/70"
                title={t('header.tapToEdit')}
              >
                <span className="truncate text-[19px] font-bold tracking-tight text-room-text">
                  {displayRoomName}
                </span>
                <Edit2
                  size={13}
                  strokeWidth={2.25}
                  className="shrink-0 text-room-text-faint transition-colors group-hover:text-room-text-dim"
                  aria-hidden
                />
              </button>
            ) : (
              <h1 className="truncate text-[19px] font-bold tracking-tight text-room-text">
                {displayRoomName}
              </h1>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <SettingsButton variant="room" />
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleShare}
              className="flex h-[38px] w-[38px] items-center justify-center rounded-[11px] bg-room-accent text-[#06231D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-room-accent"
              title={copied ? t('share.linkCopied') : t('share.copyRoomLink')}
              aria-label={t('share.copyRoomLink')}
            >
              <Share2 size={17} strokeWidth={2} />
            </motion.button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <motion.header
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="sticky top-0 z-50 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <div className="hidden h-16 items-center justify-between sm:flex">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center gap-3 transition-opacity hover:opacity-90">
              <BrandLogoLink />
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('common.appName')}</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('common.tagline')}</p>
              </div>
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSelector />
            <ThemeToggle />
          </div>
        </div>

        <div className="py-3 sm:hidden">
          <div className="flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2 transition-opacity hover:opacity-90">
              <BrandLogoLink size="sm" />
              <h1 className="text-sm font-bold text-gray-900 dark:text-white">{t('common.appName')}</h1>
            </Link>
            <div className="flex items-center gap-2">
              <SettingsButton />
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
}
